import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// ConfirmDialog reemplaza window.confirm/prompt con un modal del design
// system. Provee:
// - <ConfirmDialogProvider> en root.
// - useConfirm() — abre confirm; resuelve Promise<boolean>.
// - usePrompt() — abre prompt con input; resuelve Promise<string | null>.

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
}

export interface PromptOptions extends ConfirmOptions {
  // Texto sobre el input.
  inputLabel?: string;
  // Placeholder del input.
  placeholder?: string;
  // Valor inicial del input.
  defaultValue?: string;
  // Validación opcional. Devolver string para mostrar mensaje de error y
  // bloquear el confirm. Devolver null/undefined si es válido.
  validate?: (value: string) => string | null | undefined;
}

interface DialogState {
  options: ConfirmOptions;
  // Si está presente, el dialog incluye un input (modo prompt).
  prompt?: PromptOptions;
  resolve: (value: { ok: boolean; value?: string }) => void;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export const ConfirmDialogProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<DialogState | null>(null);
  // Ref para evitar resolver dos veces si el dialog se cierra por backdrop
  // y por botón cancel casi simultáneamente.
  const resolvedRef = useRef(false);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolvedRef.current = false;
      setState({
        options,
        resolve: ({ ok }) => resolve(ok),
      });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      resolvedRef.current = false;
      setState({
        options,
        prompt: options,
        resolve: ({ ok, value }) => resolve(ok ? (value ?? '') : null),
      });
    });
  }, []);

  const handleClose = useCallback((ok: boolean, value?: string) => {
    setState((curr) => {
      if (!curr || resolvedRef.current) return curr;
      resolvedRef.current = true;
      curr.resolve({ ok, value });
      return null;
    });
  }, []);

  const value = useMemo<ConfirmContextValue>(() => ({ confirm, prompt }), [confirm, prompt]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog state={state} onClose={handleClose} />
    </ConfirmContext.Provider>
  );
};

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm debe usarse dentro de <ConfirmDialogProvider>');
  }
  return ctx.confirm;
}

export function usePrompt(): (options: PromptOptions) => Promise<string | null> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('usePrompt debe usarse dentro de <ConfirmDialogProvider>');
  }
  return ctx.prompt;
}

interface ConfirmDialogInternalProps {
  state: DialogState | null;
  onClose: (ok: boolean, value?: string) => void;
}

const ConfirmDialog = ({ state, onClose }: ConfirmDialogInternalProps) => {
  // Estado controlado del input. Se sincroniza al abrir cada nuevo prompt.
  const [inputValue, setInputValue] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const promptDefault = state?.prompt?.defaultValue ?? '';
  const promptOpen = state?.prompt !== undefined;

  // Reset al abrir nuevo prompt.
  useEffect(() => {
    if (promptOpen) {
      setInputValue(promptDefault);
      setValidationError(null);
    }
  }, [promptOpen, promptDefault]);

  if (!state) return <></>;

  const { options, prompt } = state;
  const isDestructive = options.variant === 'destructive';

  function handleConfirm(): void {
    if (prompt) {
      const trimmed = inputValue.trim();
      if (trimmed.length === 0) {
        setValidationError('Este campo es obligatorio.');
        return;
      }
      if (prompt.validate) {
        const err = prompt.validate(trimmed);
        if (err) {
          setValidationError(err);
          return;
        }
      }
      onClose(true, trimmed);
      return;
    }
    onClose(true);
  }

  return (
    <Dialog.Root
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose(false);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'border-border bg-surface rounded-lg border p-6 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
          aria-describedby={undefined}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Dialog.Title className="text-text text-lg font-semibold">
                {options.title}
              </Dialog.Title>
              {options.description && (
                <Dialog.Description className="text-text-muted text-sm">
                  {options.description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="text-text-muted hover:text-text transition-colors"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {prompt && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              className="space-y-2"
            >
              {prompt.inputLabel && (
                <Label htmlFor="confirm-dialog-input">{prompt.inputLabel}</Label>
              )}
              <Input
                id="confirm-dialog-input"
                value={inputValue}
                placeholder={prompt.placeholder}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  if (validationError) setValidationError(null);
                }}
                autoFocus
                aria-invalid={validationError !== null}
              />
              {validationError && (
                <p role="alert" className="text-danger text-xs">
                  {validationError}
                </p>
              )}
            </form>
          )}

          <div className="mt-6 flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onClose(false)}>
              {options.cancelLabel ?? 'Cancelar'}
            </Button>
            <Button
              type="button"
              variant={isDestructive ? 'destructive' : 'default'}
              onClick={handleConfirm}
              autoFocus={!prompt}
            >
              {options.confirmLabel ?? (isDestructive ? 'Eliminar' : 'Confirmar')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
